import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { CircularProgress } from '@mui/material';
import { X } from 'lucide-react';

import Button from 'components/Button/Button';

import { EToasterStatus, showToast } from '../../utils/showToast';

import {
    IMPORT_CSV_FILE_DESCRIPTION,
    IMPORT_CSV_FILE_MESSAGE
} from './constants/constants';
import { ImportCSVContentProps } from './types';

const ImportCSVContent = ({
    isUploading,
    fileRef,
    setFieldValue
}: ImportCSVContentProps) => {
    const handleFileChange = (event: any) => {
        const file = event.currentTarget.files[0];
        if (file.type !== 'text/csv') {
            showToast({
                message: IMPORT_CSV_FILE_MESSAGE,
                status: EToasterStatus.ERROR
            });
            return;
        }
        setFieldValue('file', file);
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        const files = event.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (file.type !== 'text/csv') {
                showToast({
                    message: IMPORT_CSV_FILE_MESSAGE,
                    status: EToasterStatus.ERROR
                });
                return;
            }
            setFieldValue('file', file);
            if (fileRef.current) {
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                fileRef.current.files = dataTransfer.files;
            }
        }
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
    };

    const handleDragEnter = (event: React.DragEvent) => {
        event.preventDefault();
    };

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault();
    };

    const calculateFileSizeDisplay = (sizeInBytes: number) => {
        const sizeInKB = sizeInBytes / 1024;
        if (sizeInKB < 1024) {
            return sizeInKB.toFixed(2) + ' KB';
        } else {
            const sizeInMB = sizeInKB / 1024;
            return sizeInMB.toFixed(2) + ' MB';
        }
    };

    return (
      <div>
        <div>
          <input
            style={{ display: 'none' }}
            type="file"
            ref={fileRef}
            accept=".csv"
            onChange={handleFileChange}
          />
          <div
            className="relative flex flex-col items-center justify-center h-72 bg-base-muted border-2 border-dashed border-base-border rounded-lg gap-2"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
          >
            {isUploading ? (
              <CircularProgress />
            ) : !!fileRef?.current?.files?.length ? (
              <>
                <button
                  className="absolute top-4 right-4 text-base-mutedForeground hover:text-base-foreground transition-colors"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (fileRef.current) {
                      fileRef.current.value = '';
                    }
                    setFieldValue('file', null);
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
                <FontAwesomeIcon
                  icon="fa-solid fa-file"
                  style={{
                    fontSize: '34px',
                    height: '34px',
                    width: '34px',
                    color: '#3F3F46'
                  }}
                />
                <div className="text-sm font-medium text-base-foreground">
                  {fileRef?.current?.files[0].name}
                </div>
                <div className="text-xs text-base-mutedForeground">
                  {calculateFileSizeDisplay(fileRef.current.files[0].size)}
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-2.5 items-center justify-center">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    fileRef?.current?.click();
                  }}
                  leftIcon={
                    <FontAwesomeIcon
                      icon="fa-solid fa-upload"
                      className="text-sm"
                    />
                  }
                >
                  Upload
                </Button>
                <p className="text-sm text-base-mutedForeground">
                  {IMPORT_CSV_FILE_DESCRIPTION}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
};

export default ImportCSVContent;

